/* eslint-disable camelcase */
import React from 'react';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import Grid from '@mui/material/Grid';

import { Formik } from 'formik';
import RoundedButton from '@app/components/atoms/RoundedButton';
import TextField from '../../../atoms/TextField';
import Link from '../../../atoms/Link';
import Recaptcha from '../../../atoms/Recaptcha';

import { SignInContainer, StyledForm, ButtonContainer, useClasses } from './style';
import { redirectToRoute, setAuthCookie } from '../../../../helpers';
import MutationsStatus from '../../../molecules/MutationsStatus';
import { useTranslation } from '../../../../config/i18n';
import Fragemets from '../../../../apollo/fragments';

const REGISTER_MUTATION = gql`
  mutation register($input: RegisterInput) {
    register(input: $input) {
      access_token
      user {
        ...UserInformation
      }
    }
  }
  ${Fragemets.userInfo}
`;

const SignupForm = () => {
  const client = useApolloClient();

  const [
    registerNewUser,
    { loading: mutationLoading, error: mutationError, data: mutationData },
  ] = useMutation(REGISTER_MUTATION);

  const { t } = useTranslation('common');

  const classes = useClasses()

  const registerAccount = input => {
    registerNewUser({
      variables: { input },
    });
  };

  if (!mutationLoading && mutationData && mutationData.register) {
    const { access_token, user } = mutationData.register;
    setAuthCookie(access_token, false);
    client.cache.reset().then(() => {
      client.writeQuery({
        query: gql`
          {
            me
          }
        `,
        data: { me: user },
      });
      redirectToRoute({}, 'register_complete');
    });
  }

  return (
    <>
      <MutationsStatus loading={mutationLoading} error={mutationError} success={mutationData} />

      <Grid container spacing={1}>
        <Formik
          initialValues={{
            name: '',
            email: '',
            password: '',
            password_confirmation: '',
            recaptcha: null,
          }}
          validate={(values) => {
            const errors = {};
            if (!values.name) {
              errors.name = t('validation.req');
            } else if (values.name.trim().split(' ').length < 2) {
              errors.name = t('common:validation.at_least_two_words');
            }
            if (!values.email) {
              errors.email = t('validation.req');
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
              errors.email = t('validation.email');
            }

            if (!values.password) {
              errors.password = t('validation.req');
            } else if (values.password !== values.password_confirmation) {
              errors.password_confirmation = t('validation.password');
            }

            if (!values.recaptcha) {
              errors.recaptcha = t('validation.req');
            }

            return errors;
          }}
          onSubmit={(values) => {
            registerAccount(values);
            // setTimeout(() => {
            //   alert(JSON.stringify(values, null, 2));
            //   setSubmitting(false);
            // }, 400);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            /* and other goodies */
          }) => (
            <StyledForm>
              <Grid item xs={12}>
                <TextField
                  id="name"
                  name="name"
                  label={t('full_name')}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                  helperText={touched.name && errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="email"
                  name="email"
                  label={t('email')}
                  value={values.email}
                  type="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email}
                  helperText={touched.email && errors.email}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  id="password"
                  label={t('password')}
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="password"
                  error={touched.password && errors.password}
                  helperText={touched.password && errors.password}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="password_confirmation"
                  label={t('password_confirmation')}
                  type="password"
                  name="password_confirmation"
                  value={values.password_confirmation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  // fullWidth
                  error={touched.password_confirmation && errors.password_confirmation}
                  helperText={touched.password_confirmation && errors.password_confirmation}
                />
              </Grid>
              <Grid item xs={12} className={classes.recaptchaContainer}>
                <Recaptcha
                  name="recaptcha"
                  setValue={setFieldValue}
                  value={values.recaptcha}
                  onChange={handleChange}
                  reset={!!mutationError}
                />
              </Grid>
              <ButtonContainer container xs={12} lg={12}>
                <Grid item lg={10}>
                  <RoundedButton
                    disabled={mutationLoading}
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    loading={mutationLoading}
                    fullWidth
                    type="submit"
                    sx={{
                      backgroundColor: '#3949AB'
                    }}
                  >
                    {t('register')}
                  </RoundedButton>
                </Grid>
              </ButtonContainer>
              <SignInContainer item xs={12}>
                {t('have_account')} <Link href="login">{t('login')}</Link>
              </SignInContainer>
            </StyledForm>
          )}
        </Formik>
      </Grid>
    </>
  );
};

SignupForm.propTypes = {};

export default SignupForm;
